import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  // --- CONFIGURATION ---
  // We use Localhost for testing because Render doesn't have the new code yet.
  const API_BASE_URL = 'https://my-space-backend1.onrender.com'; 

  // --- LOGIN STATES ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedInUserEmail, setLoggedInUserEmail] = useState('');
  const [loginError, setLoginError] = useState('');

  // --- DASHBOARD STATES ---
  const [currentTab, setCurrentTab] = useState('view'); 
  const [students, setStudents] = useState([]); 
  
  const [formData, setFormData] = useState({
    student_name: '',
    sex: '',
    father_name: '',
    mother_name: '',
    dob: '',
    joining_date: '',
    class_details: ''
  });

  // --- 1. INITIAL CHECKS & DATA FETCHING ---
  
  // Check login status on load
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user_status`, {
          method: 'GET',
          credentials: 'include'
        });
        const data = await response.json();
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
          setLoggedInUserEmail(data.email);
          fetchStudents(); // Fetch students immediately if logged in
        }
      } catch (error) {
        console.error('Error checking login status:', error);
      }
    };
    checkLoginStatus();
  }, []);

  // Function to fetch students from Database
  const fetchStudents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/students`);
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
      } else {
        console.error("Failed to fetch students");
      }
    } catch (error) {
      console.error("Network error fetching students:", error);
    }
  };

  // --- 2. HANDLERS ---

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (response.ok) {
        setIsLoggedIn(true);
        setLoggedInUserEmail(data.email);
        setEmail('');
        setPassword('');
        fetchStudents(); // Load data after successful login
      } else {
        setLoginError(data.message || 'Login failed.');
      }
    } catch (error) {
      setLoginError('Network error.');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/logout`, { method: 'POST', credentials: 'include' });
      setIsLoggedIn(false);
      setLoggedInUserEmail('');
      setStudents([]); 
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Form Input Handler
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // --- SAVE STUDENT TO DATABASE ---
  const handleAddStudentSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        alert("Success! Student saved to Supabase Database.");
        
        // Refresh the list so the new student appears
        fetchStudents(); 
        
        // Clear form
        setFormData({
          student_name: '', sex: '', father_name: '', mother_name: '', dob: '', joining_date: '', class_details: ''
        });
        
        // Switch to View tab
        setCurrentTab('view');
      } else {
        alert("Error saving student: " + result.error);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Network error. Check console.");
    }
  };

  // --- 3. UI RENDERING ---
  return (
    <div className="App">
      {!isLoggedIn ? (
        <div className="login-container">
          <h2>Login to Little Bloomers</h2>
          <form onSubmit={handleLoginSubmit}>
            <div className="form-group">
              <label>Email:</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {loginError && <p className="error-message">{loginError}</p>}
            <button type="submit">Login</button>
          </form>
        </div>
      ) : (
        <div className="dashboard-container">
          <header className="dashboard-header">
            <h2>Little Bloomers Dashboard</h2>
            <div className="user-info">
              <span>{loggedInUserEmail}</span>
              <button onClick={handleLogout} className="logout-btn">Logout</button>
            </div>
          </header>

          <div className="tabs">
            <button 
              className={currentTab === 'view' ? 'active-tab' : ''} 
              onClick={() => { setCurrentTab('view'); fetchStudents(); }} 
            >
              View Students
            </button>
            <button 
              className={currentTab === 'add' ? 'active-tab' : ''} 
              onClick={() => setCurrentTab('add')}
            >
              Add Student
            </button>
          </div>

          <div className="tab-content">
            {currentTab === 'view' && (
              <div className="view-section">
                <h3>Student List</h3>
                {students.length === 0 ? (
                  <p>No students found in database.</p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Class</th>
                        <th>Sex</th>
                        <th>Father</th>
                        <th>Mother</th>
                        <th>DOB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student, index) => (
                        <tr key={index}>
                          <td>{student.student_name}</td>
                          <td>{student.class_details}</td>
                          <td>{student.sex}</td>
                          <td>{student.father_name}</td>
                          <td>{student.mother_name}</td>
                          <td>{student.dob}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {currentTab === 'add' && (
              <div className="add-section">
                <h3>Add New Student</h3>
                <form onSubmit={handleAddStudentSubmit} className="student-form">
                  <div className="form-row">
                    <label>Student Name:</label>
                    <input name="student_name" value={formData.student_name} onChange={handleFormChange} required />
                  </div>
                  <div className="form-row">
                    <label>Sex:</label>
                    <select name="sex" value={formData.sex} onChange={handleFormChange} required>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div className="form-row">
                    <label>Father's Name:</label>
                    <input name="father_name" value={formData.father_name} onChange={handleFormChange} required />
                  </div>
                  <div className="form-row">
                    <label>Mother's Name:</label>
                    <input name="mother_name" value={formData.mother_name} onChange={handleFormChange} required />
                  </div>
                  <div className="form-row">
                    <label>Date of Birth:</label>
                    <input type="date" name="dob" value={formData.dob} onChange={handleFormChange} required />
                  </div>
                  <div className="form-row">
                    <label>Joining Date:</label>
                    <input type="date" name="joining_date" value={formData.joining_date} onChange={handleFormChange} required />
                  </div>
                  <div className="form-row">
                    <label>Class Details:</label>
                    <input name="class_details" value={formData.class_details} onChange={handleFormChange} required />
                  </div>
                  <button type="submit" className="save-btn">Save Student Details</button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;